import type { AutoChinBotSnapshot } from '@/lib/auto-chin-bot-engine';
import type { AutoChinSymbolConfig } from '@/hooks/useAutoChinWorkspacePersistence';
import { cn, type AutoChinSymbolList } from './types';

export type AutoChinConfigField = 'bigCandlePips' | 'st50Pips';

interface AutoChinSymbolTableProps {
  selectedList: AutoChinSymbolList;
  symbols: AutoChinSymbolList['symbols'];
  engineState: AutoChinBotSnapshot | null;
  symbolConfigs: Record<number, AutoChinSymbolConfig>;
  connected: boolean;
  onConfigChange: (symbolId: number, field: AutoChinConfigField, value: number | null) => void;
}

function NumInput({
  value,
  onCommit,
  title,
  placeholder = '—',
}: {
  value: number | undefined;
  onCommit: (v: number | null) => void;
  title: string;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center gap-1.5" title={title}>
      <input
        type="number"
        min={0}
        step={0.1}
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onCommit(null);
          const n = Number(raw);
          onCommit(isNaN(n) ? null : n);
        }}
        placeholder={placeholder}
        className="w-16 px-2 py-1 bg-background-50 border border-background-300/60 rounded-md text-xs font-mono text-right text-foreground-900 focus:outline-none focus:border-primary-500/60 focus:ring-1 focus:ring-primary-500/20"
      />
      <span className="text-[10px] text-foreground-400">pips</span>
    </div>
  );
}

export default function AutoChinSymbolTable({
  selectedList,
  symbols,
  engineState,
  symbolConfigs,
  connected,
  onConfigChange,
}: AutoChinSymbolTableProps) {
  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-background-200/70 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
            <i className="ri-table-line text-primary-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">Control Center</h3>
            <p className="text-[11px] text-foreground-500">
              {selectedList.name} · {symbols.length} symbols
            </p>
          </div>
        </div>
        <span className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium',
          connected ? 'bg-emerald-500/10 text-emerald-400' : 'bg-background-200/70 text-foreground-400'
        )}>
          <span className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-emerald-400 animate-pulse' : 'bg-foreground-300')}></span>
          {connected ? 'Live stream' : 'Idle'}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-50">
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide">Symbol</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Big Candle</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">ST50</th>
            </tr>
          </thead>
          <tbody>
            {symbols.map((sym) => {
              const buf = engineState?.symbols[sym.symbolId];
              const cfg = symbolConfigs[sym.symbolId] || {};
              return (
                <tr
                  key={sym.symbolId}
                  className="border-b border-background-200/40 last:border-b-0 hover:bg-background-50/60 transition-colors"
                >
                  {/* Symbol */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-accent-500/70"></span>
                      <span className="text-sm font-heading font-semibold text-foreground-900">{sym.symbolName}</span>
                      <span className="text-[10px] text-foreground-400 font-mono">#{sym.symbolId}</span>
                      {buf && !buf.loaded && (
                        <span className="text-[9px] text-amber-500 font-medium">loading</span>
                      )}
                    </div>
                  </td>

                  {/* Big Candle (task1) */}
                  <td className="px-4 py-3">
                    <NumInput
                      value={cfg.bigCandlePips}
                      onCommit={(v) => onConfigChange(sym.symbolId, 'bigCandlePips', v)}
                      title="task1: ngưỡng thân nến (pips) để tính là nến xu hướng"
                    />
                  </td>

                  {/* ST50 (task3) */}
                  <td className="px-4 py-3">
                    <NumInput
                      value={cfg.st50Pips}
                      onCommit={(v) => onConfigChange(sym.symbolId, 'st50Pips', v)}
                      placeholder="5"
                      title="task3: khoảng cách tối đa SMA50 ↔ TMA50 (pips) để tính hội tụ ST50 — mặc định 5"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}